import Ajv from 'ajv';
import { z } from 'zod';
import { CandidateOpportunitiesConst, CurrencyMap } from '@axiom/const';
import { SchemaTimestamp, ZodArrayToEnum } from '@axiom/types';

import { axiomValidationOptions } from './options';
import { PositionSchema } from './position';
import { OpportunitySchema } from './opportunity';
import { CandidateReputationClarificationSchema } from './candidate-reputation-clarification';
import { ClientRequestSchema } from './client-request';
import {
  CandidateOpportunityCandidateStatuses,
  ZodCandidateOpportunityCandidateStatuses,
} from './candidate-opportunities-candidate-statuses';
import { CandidateOpportunitiesCandidateSchema } from './candidate-opportunities-candidate';
import { CandidateReputationSchema } from './candidate-reputation';
import { AhqSubmissionCandidateExperienceSchema } from './ahq-submission-candidate-experience';

const CompensationValues = Object.values(
  CandidateOpportunitiesConst.CompensationTypes
);

export const ZodCompensationValues = z.enum(ZodArrayToEnum(CompensationValues));

export const CandidateOpportunityDisplayBillingRates = Object.values(
  CandidateOpportunitiesConst.DisplayBillingRate
);

export const ZodBillingRateValues = z.enum(
  ZodArrayToEnum(CandidateOpportunityDisplayBillingRates)
);

const CandidateOpportunityRejectionLossCodes = Object.values(
  CandidateOpportunitiesConst.CandidateRejectionLossCodesValues
);

export const ZodRejectionLossCodeValues = z.enum(
  ZodArrayToEnum(CandidateOpportunityRejectionLossCodes)
);

export const CandidateCurrencyTypes = Object.keys(CurrencyMap.codes) as Array<
  keyof typeof CurrencyMap.codes
>;

export const BaseCandidateOpportunitySchema = z.object({
  annualSalary: z.number().nonnegative().nullable(),
  baseHourlyCompensation: z.number().nonnegative().nullable(),
  calculatedDisplayName: z.string().nullable(),
  candidateAvailabilityNotes: z.string().max(1000).nullable(),
  candidateBaseAnnualSalary: z.number().positive().nullable(),
  candidateBonusPercentage: z.number().nonnegative().nullable(),
  candidateEngagedAtLowerEsc: z.boolean(),
  candidateExperienceNotes: z.string().max(1000).nullable(),
  candidateExploringOutsideRoles: z.string().nullable(),
  candidateId: z.string().uuid(),
  candidateIsInterested: z.boolean().default(false),
  candidatePlannedTimeOffNotes: z.string().max(1000).nullable(),
  candidateReasonForNo: z.string().max(255).nullable(),
  candidateReputation: CandidateReputationSchema.nullable(),
  candidateReputationClarification:
    CandidateReputationClarificationSchema.nullable(),
  candidateStatus: ZodCandidateOpportunityCandidateStatuses.nullable(),
  candidateStatusMessage: z.string().nullable(),
  candidateStatusMessageDismissedAt: SchemaTimestamp.nullable(),
  candidateStatusUpdatedAt: SchemaTimestamp.nullable(),
  viewedOnHistoryAt: SchemaTimestamp.nullable(),
  candidateWorkscheduleAnnualHours: z.number().nonnegative().nullable(),
  canDisplayReputationData: z.boolean(),
  compensation: z.string().nullable(),
  compensationType: z.enum(ZodArrayToEnum(CompensationValues)).nullable(),
  consultant1099UtilizationWarning: z.boolean().nullable(),
  createdAt: SchemaTimestamp,
  createdBy: z.string().uuid().nullable(),
  currencyType: z.enum(ZodArrayToEnum(CandidateCurrencyTypes)).nullable(),
  displayBillingRate: z
    .enum(ZodArrayToEnum(CandidateOpportunityDisplayBillingRates))
    .nullable(),
  ebillingMandateMultiple: z.number().nonnegative().nullable(),
  exchangeRate: z.number().nonnegative().nullable(),
  hasBeenWarmed: z.boolean().nullable(),
  hasNoConflictOfInterest: z.boolean().nullable(),
  hourlyCompensation: z.string().nullable(),
  hourlyRate: z.number().nonnegative().nullable(),
  hourlyRateBurdened: z.number().nonnegative(),
  hourlySalary: z.number().nonnegative().nullable(),
  hoursPerDay: z.number().nonnegative().nullish(),
  id: z.string().uuid(),
  isActiveForSubmission: z.boolean().nullable(),
  isPriorityTalentAtSelected: z.boolean().nullable(),
  isCrossFulfilled: z.boolean().nullable(),
  lastUpdatedBy: z.string().uuid().nullable(),
  marginApprovalCeiling: z.number().nonnegative().nullable(),
  marginApprovalFloor: z.number().nonnegative().nullable(),
  marginFloorPct: z.number().nullable(),
  marginTargetPct: z.number().nullable(),
  marginUpperPct: z.number().nullable(),
  meetsMinPayRequirements: z.boolean().nullable(),
  openToLowerEsc: z.boolean().nullable(),
  openToLowerEscCompensationPercentage: z.number().nullable(),
  opportunityId: z.string().uuid(),
  pinnedByClient: z.boolean().default(false),
  position: PositionSchema.nullish(),
  positionId: z.string().uuid().nullable(),
  previousCandidateStatus: ZodCandidateOpportunityCandidateStatuses.nullable(),
  previousPositiveCandidateStatus:
    ZodCandidateOpportunityCandidateStatuses.nullable(),
  productBurdenRateMultiple: z.number().nonnegative().nullable(),
  productPremiumRateMultiple: z.number().nonnegative().nullable(),
  proposedCostTarget: z.number().nonnegative().nullable(),
  proposedHourlyCompensation: z.number().nonnegative().nullable(),
  proposedHourlyCompCandidateCurrency: z.number().nonnegative().nullable(),
  proposedHourlyRate: z.number().nonnegative().nullish(),
  proposedMarginTarget: z.number().nullable(),
  rejectionLossCode: z
    .enum(
      ZodArrayToEnum(
        CandidateOpportunitiesConst.CandidateRejectionLossCodesValues
      )
    )
    .nullable(),
  rejectionLossCodeCustomMessage: z.string().nullable(),
  salesforceQuoteLineId: z.string().max(255).nullable(),
  showCandidateReputation: z.boolean().default(false),
  showReputation: z.boolean().nullable(),
  submissionDescription: z.string().nullable(),
  submissionRank: z.number().nonnegative().nullable(),
  updatedAt: SchemaTimestamp.nullable(),
});

export type BaseCandidateOpportunity = z.infer<
  typeof BaseCandidateOpportunitySchema
>;

export const CandidateOpportunitySchema = BaseCandidateOpportunitySchema.pick({
  annualSalary: true,
  baseHourlyCompensation: true,
  calculatedDisplayName: true,
  candidateAvailabilityNotes: true,
  candidateBaseAnnualSalary: true,
  candidateBonusPercentage: true,
  candidateEngagedAtLowerEsc: true,
  candidateExperienceNotes: true,
  candidateExploringOutsideRoles: true,
  candidateId: true,
  candidateIsInterested: true,
  candidatePlannedTimeOffNotes: true,
  candidateReputationClarification: true,
  candidateStatus: true,
  candidateStatusMessage: true,
  candidateStatusUpdatedAt: true,
  viewedOnHistoryAt: true,
  compensation: true,
  compensationType: true,
  consultant1099UtilizationWarning: true,
  createdAt: true,
  currencyType: true,
  displayBillingRate: true,
  ebillingMandateMultiple: true,
  exchangeRate: true,
  hasBeenWarmed: true,
  hasNoConflictOfInterest: true,
  hourlyCompensation: true,
  hourlyRate: true,
  hourlyRateBurdened: true,
  hourlySalary: true,
  hoursPerDay: true,
  id: true,
  isActiveForSubmission: true,
  isPriorityTalentAtSelected: true,
  lastUpdatedBy: true,
  marginApprovalCeiling: true,
  marginApprovalFloor: true,
  marginFloorPct: true,
  marginTargetPct: true,
  marginUpperPct: true,
  meetsMinPayRequirements: true,
  openToLowerEsc: true,
  openToLowerEscCompensationPercentage: true,
  opportunityId: true,
  pinnedByClient: true,
  position: true,
  positionId: true,
  previousCandidateStatus: true,
  previousPositiveCandidateStatus: true,
  productBurdenRateMultiple: true,
  productPremiumRateMultiple: true,
  proposedCostTarget: true,
  proposedHourlyCompensation: true,
  proposedHourlyCompCandidateCurrency: true,
  proposedHourlyRate: true,
  proposedMarginTarget: true,
  rejectionLossCode: true,
  rejectionLossCodeCustomMessage: true,
  showReputation: true,
  showCandidateReputation: true,
  submissionDescription: true,
  submissionRank: true,
}).extend({
  clientRequest: ClientRequestSchema.nullable(),
  badge: z.string().nullable(),
  // Created independent schema to fix circular dependency issue
  candidate: CandidateOpportunitiesCandidateSchema,
  isInstantMatch: z.boolean().nullish(),
});

export type CandidateOpportunity = z.infer<typeof CandidateOpportunitySchema>;

export const OpportunityCandidateSchema = OpportunitySchema.merge(
  BaseCandidateOpportunitySchema
).extend({
  clientRequest: ClientRequestSchema.nullable(),
  experiences: z.array(AhqSubmissionCandidateExperienceSchema).nullish(),
  isPriorityTalent: z.boolean(),
  profileImageKey: z.string().max(255).nullable(),
  yearsOfExperience: z.number().nullable(),
});

export type OpportunityCandidate = z.infer<typeof OpportunityCandidateSchema>;

export const OpportunityCandidateOrMlRecommendedSchema =
  OpportunityCandidateSchema.extend({
    isMlRecommendation: z.boolean(),
  });

export type OpportunityCandidateOrMlRecommended = z.infer<
  typeof OpportunityCandidateOrMlRecommendedSchema
>;

// AJV stuff below =============================================================

const ajv = new Ajv(axiomValidationOptions());
const {
  CandidateRejectionLossCodes,
  CandidateStatuses,
  DisplayBillingRate,
  CompensationTypes,
  ReasonForNo,
} = CandidateOpportunitiesConst;

const candidateOpportunitiesValidation = {
  type: 'object',
  additionalProperties: false,
  properties: {
    candidateStatus: {
      type: 'string',
      enum: Object.values(CandidateStatuses),
    },
    candidateStatusMessage: {
      type: ['string', 'null'],
      maxLength: 1000,
    },
    endDate: { type: 'string', format: 'date' },
    candidateReasonForNo: {
      type: ['string', 'null'],
      enum: Object.values(ReasonForNo),
    },
    candidateAvailabilityNotes: {
      type: ['string', 'null'],
      maxLength: 1000,
    },
    candidateExperienceNotes: {
      type: ['string', 'null'],
      maxLength: 1000,
    },
    compensationType: {
      type: ['string', 'null'],
      enum: Object.values(CompensationTypes),
    },
    experiences: {
      type: ['array', 'null'],
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          id: {
            type: 'string',
            format: 'uuid',
          },
          isHighlighted: {
            type: ['boolean', 'null'],
          },
          experienceVisible: {
            type: 'boolean',
          },
          submissionDescription: {
            type: ['string', 'null'],
          },
        },
      },
    },
    displayBillingRate: {
      type: ['string', 'null'],
      enum: Object.keys(DisplayBillingRate),
    },
    rejectionLossCode: {
      type: ['string', 'null'],
      enum: Object.values(CandidateRejectionLossCodes),
    },
    rejectionLossCodeCustomMessage: {
      type: ['string', 'null'],
    },
    pinnedByClient: {
      type: 'boolean',
    },
    positionId: {
      type: ['string', 'null'],
      format: 'uuid',
    },
    previousCandidateStatus: {
      type: 'string',
      enum: Object.values(CandidateOpportunityCandidateStatuses),
    },
    proposedHourlyCompCandidateCurrency: {
      type: 'number',
      maximum: Number.MAX_SAFE_INTEGER,
    },
    proposedHourlyCompensation: {
      type: 'number',
      maximum: Number.MAX_SAFE_INTEGER,
    },
    proposedHourlyRate: {
      type: 'number',
      maximum: Number.MAX_SAFE_INTEGER,
    },
    proposedMarginTarget: {
      type: 'number',
      maximum: Number.MAX_SAFE_INTEGER,
    },
    salesforceQuoteLineId: {
      type: ['string', 'null'],
      maxLength: 255,
    },
    startDate: {
      type: ['string', 'null'],
      format: 'date',
    },
    hasNoConflictOfInterest: {
      type: ['boolean', 'null'],
    },
    candidateExploringOutsideRoles: {
      type: ['string', 'null'],
      maxLength: 1000,
    },
    candidatePlannedTimeOffNotes: {
      type: ['string', 'null'],
      maxLength: 1000,
    },
    candidateStatusMessageDismissedAt: {
      type: ['string', 'null'],
      format: 'date-time',
    },
    viewedOnHistoryAt: {
      type: ['string', 'null'],
      format: 'date-time',
    },
    isActiveForSubmission: {
      type: 'boolean',
    },
    isPriorityTalentAtSelected: {
      type: 'boolean',
    },
  },
};

export const candidateOpportunitiesValidator = ajv.compile({
  ...candidateOpportunitiesValidation,
  anyOf: Object.keys(candidateOpportunitiesValidation.properties).map(key => ({
    required: [key],
  })),
});
